import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blog-posts';

const BlogPage = () => {
  const [selectedCategories, setSelectedCategories] = useState([]);

  const categories = useMemo(() => {
    const allCategories = new Set();
    blogPosts.forEach(post => {
      post.category.split(',').forEach(cat => {
        allCategories.add(cat.trim());
      });
    });
    return Array.from(allCategories).sort();
  }, []);

  const filteredPosts = useMemo(() => {
    if (selectedCategories.length === 0) {
      return blogPosts;
    }
    return blogPosts.filter(post => {
      const postCategories = post.category.split(',').map(c => c.trim());
      return selectedCategories.some(selectedCat => postCategories.includes(selectedCat));
    });
  }, [selectedCategories]);

  const handleCategoryClick = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
  };

  const handleResetFilters = () => {
    setSelectedCategories([]);
  };

  return (
    <div className="bg-slate-50 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">From the Blog</h2>
          <p className="mt-2 text-lg leading-8 text-gray-600">
            Read the latest articles on web development, design, and business.
          </p>
        </div>

        <div className="my-12 flex justify-center flex-wrap gap-4">
          <button
            key="all"
            onClick={handleResetFilters}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ease-in-out ${
              selectedCategories.length === 0
                ? 'bg-blue-600 text-white shadow'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            All
          </button>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => handleCategoryClick(category)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ease-in-out ${
                selectedCategories.includes(category)
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {filteredPosts.map((post) => (
            <Link to={`/blog/${post.slug}`} key={post.id} className="block group">
              <article className="overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-300 ease-in-out group-hover:shadow-2xl h-full flex flex-col">
                <div className="relative w-full overflow-hidden">
                  <img
                    src={post.imageUrl}
                    alt=""
                    className="aspect-[16/9] w-full bg-gray-100 object-cover sm:aspect-[2/1] lg:aspect-[3/2] transition-transform duration-500 ease-in-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10" />
                </div>
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center gap-x-4 text-xs">
                    <time dateTime={post.date} className="text-gray-500">
                      {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </time>
                    <span className="relative z-10 rounded-full bg-blue-50 px-3 py-1.5 font-medium text-blue-600">
                      {post.type}
                    </span>
                  </div>
                  <div className="relative">
                    <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900">{post.title}</h3>
                    <p className="mt-4 line-clamp-3 text-sm leading-6 text-gray-600">{post.excerpt}</p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {post.category.split(',').map((cat) => (
                      <span key={cat} className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                        {cat.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogPage; 